import React, {
  useState,
  ChangeEvent,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'app/store/storeHooks';

// Actions
import { hideSidebar } from 'app/modules/sidebar/slice';

// Components
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import SidebarTableConfigItem from 'app/modules/sidebar/components/SidebarTableConfigItem';

// Selectors
import {
  U21Button,
  U21Spacer,
  U21TextField,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { selectAgent } from 'app/modules/session/selectors';
import { useReportDashboardsStorage } from 'app/modules/reportDashboards/hooks/useReportDashboardsStorage';
import { DEFAULT_REPORT_CHARTS } from 'app/modules/reportDashboards/constants';
import {
  ReportChartConfig,
  ReportSection,
} from 'app/modules/reportDashboards/models';
import { getAgentDisplayName } from 'app/modules/agents/utils';

const portal: HTMLDivElement = document.createElement('div');
document.body.appendChild(portal);

export const SidebarReportDashboardsConfig = () => {
  const dispatch = useAppDispatch();

  const { config, saveConfig, resetConfig } = useReportDashboardsStorage();
  const [pendingChartsConfig, setPendingChartsConfig] = useState<
    ReportSection[]
  >(structuredClone(config));

  const agent = useSelector(selectAgent);

  useEffect(() => {
    setPendingChartsConfig(structuredClone(config));
  }, [config]);

  const handleSave = useCallback(() => {
    if (
      pendingChartsConfig &&
      JSON.stringify(config) !== JSON.stringify(pendingChartsConfig)
    ) {
      saveConfig(structuredClone(pendingChartsConfig));

      setChartSearch(undefined);
    }
    dispatch(hideSidebar());
  }, [config, dispatch, pendingChartsConfig, saveConfig]);

  const resetToDefaultsEnabled = useMemo(
    () =>
      pendingChartsConfig &&
      JSON.stringify(DEFAULT_REPORT_CHARTS) !==
        JSON.stringify(pendingChartsConfig),
    [pendingChartsConfig],
  );

  const handleReset = async () => {
    if (!resetToDefaultsEnabled) {
      return;
    }

    resetConfig();

    dispatch(hideSidebar());
  };

  // Toggle column visibility by setting optional hidden field of TableConfig
  const toggleChart = (
    sectionIndex: number,
    checked: boolean,
    ev: ChangeEvent<HTMLInputElement>,
  ) => {
    setPendingChartsConfig((configObject): ReportSection[] => {
      const configCopy = structuredClone(configObject);

      configCopy[sectionIndex].charts = configCopy[sectionIndex].charts.map(
        (chart) => ({
          ...chart,
          enabled: chart.id === ev.target.name ? checked : chart.enabled,
        }),
      );

      return configCopy;
    });
  };

  // Swap list items on drag & drop
  const onDragEnd = (sectionIndex: number, results: DropResult) => {
    const { source, destination } = results;
    if (destination) {
      setPendingChartsConfig((configObject): ReportSection[] => {
        switch (configObject[sectionIndex].id) {
          case 'kpis': {
            const configCopy = structuredClone(configObject);
            configCopy[sectionIndex].charts = swapChartsInSection(
              configObject[sectionIndex].charts,
              source.index,
              destination.index,
            );
            return configCopy;
          }
          case 'alerts': {
            const configCopy = structuredClone(configObject);
            configCopy[sectionIndex].charts = swapChartsInSection(
              configObject[sectionIndex].charts,
              source.index,
              destination.index,
            );
            return configCopy;
          }
          case 'cases': {
            const configCopy = structuredClone(configObject);
            configCopy[sectionIndex].charts = swapChartsInSection(
              configObject[sectionIndex].charts,
              source.index,
              destination.index,
            );
            return configCopy;
          }
          case 'sars': {
            const configCopy = structuredClone(configObject);
            configCopy[sectionIndex].charts = swapChartsInSection(
              configObject[sectionIndex].charts,
              source.index,
              destination.index,
            );
            return configCopy;
          }
          default: {
            throw new Error(
              `Unexpected report section: ${configObject[sectionIndex]}`,
            );
          }
        }
      });
    }
  };

  const swapChartsInSection = <T extends ReportChartConfig>(
    charts: T[],
    sourceIndex: number,
    destinationIndex: number,
  ): T[] => {
    const reorgSectionCharts: T[] = structuredClone(charts);
    const [removed] = reorgSectionCharts.splice(sourceIndex, 1);
    reorgSectionCharts.splice(destinationIndex, 0, removed);
    return reorgSectionCharts;
  };

  const resetButton = (
    <U21Button
      variant="outlined"
      onClick={handleReset}
      disabled={!resetToDefaultsEnabled}
      tooltip={
        !resetToDefaultsEnabled
          ? undefined
          : `You (${getAgentDisplayName(agent)}) are currently on the default setting`
      }
    >
      Reset to Default
    </U21Button>
  );

  const [chartSearch, setChartSearch] = useState<string | undefined>(undefined);

  return (
    <U21SideMenu
      actionButtonProps={{ children: 'Save' }}
      onAction={handleSave}
      footerLeftContent={resetButton || undefined}
      title="Add Charts"
    >
      <U21Spacer spacing={3}>
        <U21TextField
          onChange={(v: string | undefined) => setChartSearch(v)}
          placeholder="Search charts"
          autoFocus
        />
        <U21Spacer spacing={3} dividers>
          {pendingChartsConfig.map((section, sectionIndex) => {
            const chartsList = section.charts;

            if (!chartsList?.length) {
              return null;
            }

            return (
              <U21Spacer key={section.id} spacing={3}>
                <U21Typography variant="h5">{section.title}</U21Typography>

                <DragDropContext
                  onDragEnd={(results) => onDragEnd(sectionIndex, results)}
                >
                  <Droppable droppableId="droppable">
                    {(provided) => {
                      return (
                        <ul ref={provided.innerRef}>
                          {chartsList.map((item, index) => {
                            const key = item.id;
                            return !chartSearch ||
                              item.title
                                .toLowerCase()
                                .includes(chartSearch.toLowerCase()) ? (
                              <Draggable
                                key={key}
                                draggableId={key}
                                index={index}
                              >
                                {(providedConfigItem, snapshot) => (
                                  <SidebarTableConfigItem
                                    provided={providedConfigItem}
                                    snapshot={snapshot}
                                    column={{
                                      key: item.id,
                                      label: item.title,
                                      hidden: !item.enabled,
                                    }}
                                    toggleColumns={(
                                      checked: boolean,
                                      ev: ChangeEvent<HTMLInputElement>,
                                    ) => toggleChart(sectionIndex, checked, ev)}
                                    portal={portal}
                                  />
                                )}
                              </Draggable>
                            ) : null;
                          })}
                          {provided.placeholder}
                        </ul>
                      );
                    }}
                  </Droppable>
                </DragDropContext>
              </U21Spacer>
            );
          })}
        </U21Spacer>
      </U21Spacer>
    </U21SideMenu>
  );
};
